import { Dispatch, SetStateAction, useState } from 'react';
import * as R from 'ramda';
import { Modal } from 'react-bootstrap';

import { Button } from '@Components/Button';
import { Appearance, Design, Intent } from '@Components/designConstants';
import { DEMO_GROUPINGS, OVERLAPPING_DEMO_LOOKUP } from '../linear-buying-constants';
import './AudiencePickerModal.scss';

interface SelectDemosContainerProps {
  selections: Record<string, boolean>;
  onSelect: (demoValue: string) => void;
}
const SelectDemosContainer = ({ selections, onSelect }: SelectDemosContainerProps) => (
  <div>
    {R.keys(DEMO_GROUPINGS).map(group => {
      const groupDemos = DEMO_GROUPINGS[group];
      return (
        <div className='demo-group' key={group}>
          <strong>{group}:</strong>
          <div className='demo-buttons'>
            {R.keys(groupDemos).map(prettyDemo => {
              const demoValue = groupDemos[prettyDemo];
              return (
                <Button
                  className='demo-button'
                  key={prettyDemo}
                  appearance={selections[demoValue] ? Appearance.FILLED : Appearance.OUTLINED}
                  onClick={() => onSelect(demoValue)}
                >
                  {prettyDemo}
                </Button>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

interface AudiencePickerModalProps {
  selectedDemos: Record<string, boolean>;
  setSelectedDemos: Dispatch<SetStateAction<Record<string, boolean>>>;
  onClose: () => void;
}

export const AudiencePickerModal = ({
  selectedDemos,
  setSelectedDemos,
  onClose
}: AudiencePickerModalProps) => {
  const [selections, setSelections] = useState(selectedDemos ?? {});

  const onSave = () => {
    // The Nielsen estimates query is keyed on the selected demos, so it refetches on its own.
    setSelectedDemos(R.pickBy(val => val === true, selections));
    onClose();
  };

  const onSelect = (demoValue: string) => {
    // If you select a "Meta" demo, unselect every other demo.
    if (R.values(DEMO_GROUPINGS.Meta).includes(demoValue)) {
      setSelections({ [demoValue]: true });
    } else {
      setSelections(current => {
        // If you select a demo that is not a "Meta", unselect the "Meta" demo.
        const removeMetaDemo: Record<string, boolean> = R.pickBy(
          (val, key) => !R.values(DEMO_GROUPINGS.Meta).includes(key),
          current
        );
        // Deselect overlapping demographics
        const removeOverlapDemo: Record<string, boolean> = R.pickBy(
          (val, key) => !R.values(OVERLAPPING_DEMO_LOOKUP[demoValue]).includes(key),
          removeMetaDemo
        );
        return {
          ...removeOverlapDemo,
          [demoValue]: !current[demoValue]
        };
      });
    }
  };

  return (
    <Modal show className='audience-picker-modal' onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Select Demographics</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SelectDemosContainer selections={selections} onSelect={onSelect} />
      </Modal.Body>
      <Modal.Footer>
        <div className='footer-actions'>
          <Button design={Intent.ERROR} onClick={() => setSelections({})}>
            Clear
          </Button>
          <div className='right-actions'>
            <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onSave}>Save</Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
